import React, { useState, useRef, useEffect } from 'react';
import { STATUS_OPTIONS, getStatusConfig } from '../constants';

/**
 * Custom styled status dropdown component
 * Replaces native select with a beautiful, themed dropdown
 */
const StatusDropdown = ({
    value,
    onChange,
    disabled = false,
    className = '',
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    const currentStatus = getStatusConfig(value);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Close dropdown on escape key
    useEffect(() => {
        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                setIsOpen(false);
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, []);

    const handleSelect = (statusValue) => {
        onChange?.(statusValue);
        setIsOpen(false);
    };

    return (
        <div ref={dropdownRef} className={`relative inline-block ${className}`}>
            {/* Trigger Button */}
            <button
                type="button"
                onClick={() => !disabled && setIsOpen(!isOpen)}
                disabled={disabled}
                className={`
                    flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium
                    transition-all duration-200 min-w-[130px] justify-between
                    ${disabled
                        ? 'opacity-50 cursor-not-allowed bg-gray-800 border-gray-700 text-gray-400'
                        : 'bg-gray-800 border-gray-600 text-white hover:border-gray-500 hover:bg-gray-700'}
                    ${isOpen ? 'ring-2 ring-blue-500 border-blue-500' : ''}
                `}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
            >
                <span className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${currentStatus.color === 'yellow' ? 'bg-yellow-400' :
                            currentStatus.color === 'blue' ? 'bg-blue-400' :
                                currentStatus.color === 'purple' ? 'bg-purple-400' :
                                    currentStatus.color === 'indigo' ? 'bg-indigo-400' :
                                        currentStatus.color === 'green' ? 'bg-green-400' :
                                            currentStatus.color === 'orange' ? 'bg-orange-400' :
                                                'bg-gray-400'
                        }`}></span>
                    {currentStatus.label}
                </span>
                <svg
                    className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </button>

            {/* Dropdown Menu */}
            {isOpen && (
                <div
                    className="absolute z-50 mt-1 w-full min-w-[160px] bg-gray-800 border border-gray-700 rounded-lg shadow-xl overflow-hidden animate-fadeIn"
                    role="listbox"
                >
                    {STATUS_OPTIONS.map((status) => (
                        <button
                            key={status.value}
                            type="button"
                            onClick={() => handleSelect(status.value)}
                            className={`
                                w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left
                                transition-colors duration-150
                                ${value === status.value
                                    ? 'bg-blue-900/40 text-blue-300'
                                    : 'text-gray-300 hover:bg-gray-700'}
                            `}
                            role="option"
                            aria-selected={value === status.value}
                        >
                            <span className={`w-2 h-2 rounded-full ${status.color === 'yellow' ? 'bg-yellow-400' :
                                    status.color === 'blue' ? 'bg-blue-400' :
                                        status.color === 'purple' ? 'bg-purple-400' :
                                            status.color === 'indigo' ? 'bg-indigo-400' :
                                                status.color === 'green' ? 'bg-green-400' :
                                                    status.color === 'orange' ? 'bg-orange-400' :
                                                        'bg-gray-400'
                                }`}></span>
                            {status.label}
                            {value === status.value && (
                                <svg className="w-4 h-4 ml-auto text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                            )}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default StatusDropdown;
